// 차수별현황 화면
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { fetchTurnStatus } from '../../api/profit';
import { fetchRealtimePrices } from '../../api/price';
import { fetchAccounts } from '../../api/accounts';
import PageLayout from '../../components/PageLayout';
import MultiSelectCombo from '../../components/common/MultiSelectCombo';
import TurnSellPopup from './TurnSellPopup';
import TurnBuyPopup from './TurnBuyPopup';
import StockChartPopup from '../../components/common/StockChartPopup';
import AlertQuickPopup from '../../components/common/AlertQuickPopup';
import AlertButton from '../../components/common/AlertButton';
import './TurnStatusPage.css';

// 합계/현재가/등락/차수/비고 고정 너비
const COL_QTY_W     = 75;
const COL_BPRICE_W  = 90;
const COL_BAMT_W    = 95;
const COL_EAMT_W    = 95;
const COL_EPRFT_W   = 90;
const COL_AGGRATE_W = 70;
const COL_PRICE_W   = 90;
const COL_DIFF_W    = 66;
const COL_TURN_W    = 80;
const COL_MEMO_W    = 130;

// ── 포맷 헬퍼 ─────────────────────────────────────────────────
const fPrice = v => {
  if (v == null) return '';
  const n = Number(v);
  return n % 1 !== 0
    ? n.toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : n.toLocaleString('ko-KR');
};
const fRate = v => {
  if (v == null) return '';
  const n = Number(v);
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
};
const fAmt = v => v == null ? '' : Math.round(Number(v)).toLocaleString('ko-KR');
const fQty = v => {
  if (v == null) return '';
  const n = Number(v);
  return n % 1 !== 0
    ? n.toLocaleString('ko-KR', { maximumFractionDigits: 4 })
    : n.toLocaleString('ko-KR');
};
const formatBuyDate = d => {
  if (!d) return '';
  return `${d.substring(2, 4)}.${d.substring(5, 7)}.${d.substring(8, 10)}`;
};
const getDiffStyle = v => {
  if (v == null) return {};
  return { color: v >= 0 ? '#FF0000' : '#0000FF' };
};

// ── 합계 컬럼 계산 (미매도 차수 기준) ────────────────────────
const computeTotals = row => {
  const unsold = Object.values(row.turns || {}).filter(t => t.type === 'unsold');
  if (!unsold.length) {
    return { totalQty: null, avgBuyPrice: null, totalBuyAmt: null, totalEval: null, totalProfit: null, totalRate: null };
  }
  const totalQty    = unsold.reduce((s, t) => s + (t.quantity   || 0), 0);
  const totalBuyAmt = unsold.reduce((s, t) => s + (t.buy_amount || 0), 0);
  const avgBuyPrice = totalQty > 0 ? totalBuyAmt / totalQty : null;
  const curr        = row.current_price;
  const totalEval   = curr != null && totalQty > 0 ? totalQty * curr : null;
  const totalProfit = totalEval != null ? totalEval - totalBuyAmt : null;
  const totalRate   = totalProfit != null && totalBuyAmt > 0
    ? Math.round(totalProfit / totalBuyAmt * 10000) / 100 : null;
  return { totalQty, avgBuyPrice, totalBuyAmt, totalEval, totalProfit, totalRate };
};

// ── 정렬 아이콘 ───────────────────────────────────────────────
const SortIcon = ({ col, sortKey, sortDir }) => {
  if (sortKey !== col) return <span className="sort-icon sort-idle">⇅</span>;
  return <span className="sort-icon sort-active">{sortDir === 'asc' ? '▲' : '▼'}</span>;
};

// ── 메인 컴포넌트 ─────────────────────────────────────────────
const TurnStatusPage = () => {
  const [filterAccount, setFilterAccount] = useState([]);
  const [inclSold,     setInclSold]     = useState(false);
  const [displayMode,  setDisplayMode]  = useState('rate');
  const [autoRefresh,  setAutoRefresh]  = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [popup,        setPopup]        = useState(null);
  const [chartPopup,   setChartPopup]   = useState(null);
  const [alertPopup,   setAlertPopup]   = useState(null); // {ticker, stockName, accountName, turnNo}

  const [data,         setData]         = useState([]);
  const [maxTurnNo,    setMaxTurnNo]    = useState(0);
  const [accounts,     setAccounts]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceSource,  setPriceSource]  = useState(null);
  const [message,      setMessage]      = useState({ text: '', type: '' });

  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  // 리사이즈 가능 컬럼 너비
  const [colW, setColW] = useState({ account: 90, term: 70, sector: 90, name: 200 });
  const resizingRef = useRef(null);
  const rawDataRef  = useRef([]);

  // sticky left 오프셋 (동적 계산)
  const stickyLeft = useMemo(() => {
    const { account: aw, term: tw, sector: sw, name: nw } = colW;
    const base = aw + tw + sw + nw;
    return {
      account:    0,
      term:       aw,
      sector:     aw + tw,
      name:       aw + tw + sw,
      qty:        base,
      buyPrice:   base + COL_QTY_W,
      buyAmt:     base + COL_QTY_W + COL_BPRICE_W,
      evalAmt:    base + COL_QTY_W + COL_BPRICE_W + COL_BAMT_W,
      evalProfit: base + COL_QTY_W + COL_BPRICE_W + COL_BAMT_W + COL_EAMT_W,
      totalRate:  base + COL_QTY_W + COL_BPRICE_W + COL_BAMT_W + COL_EAMT_W + COL_EPRFT_W,
    };
  }, [colW]);

  useEffect(() => {
    fetchAccounts().then(setAccounts).catch(() => {});
  }, []);

  const showMessage = (text, type = 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  // ── 팝업 핸들러 ───────────────────────────────────────────
  const openSellPopup = (row, turn, turnNo) => setPopup({
    type: 'sell',
    data: {
      buy_id: turn.buy_id, account_name: row.account_name,
      stock_name: row.stock_name, ticker: row.ticker,
      turn_no: turnNo, current_price: row.current_price,
      quantity: turn.quantity, buy_amount: turn.buy_amount, buy_price: turn.buy_price,
    },
  });

  const openBuyPopup = row => setPopup({
    type: 'buy',
    data: {
      account_name: row.account_name, stock_name: row.stock_name,
      ticker: row.ticker, current_price: row.current_price,
      target_profit_rate: row.target_profit_rate,
    },
  });

  const closePopup = () => setPopup(null);

  // ── 실시간 시세 적용 ───────────────────────────────────────
  const applyRealtimePrices = (rows, priceMap) =>
    rows.map(row => {
      const p = priceMap[row.ticker];
      if (!p || p.current_price == null) return row;
      const curr = p.current_price;
      const newTurns = {};
      for (const [k, turn] of Object.entries(row.turns || {})) {
        if (turn.type === 'unsold' && turn.buy_price != null) {
          newTurns[k] = { ...turn, profit_rate: Math.round((curr - turn.buy_price) / turn.buy_price * 10000) / 100 };
        } else {
          newTurns[k] = turn;
        }
      }
      let newOrangeCell = row.orange_cell;
      if (newOrangeCell?.last_sell_price) {
        const rate = Math.round((curr - newOrangeCell.last_sell_price) / newOrangeCell.last_sell_price * 10000) / 100;
        newOrangeCell = { ...newOrangeCell, change_rate: rate };
      }
      return { ...row, current_price: curr, diff_rate: p.diff_rate, turns: newTurns, orange_cell: newOrangeCell };
    });

  // ── 실시간 시세 갱신 ───────────────────────────────────────
  const runRealtimeUpdate = useCallback(async currentData => {
    const tickers = [...new Set(currentData.map(r => r.ticker))];
    if (!tickers.length) return;
    setPriceLoading(true);
    try {
      const priceRes = await fetchRealtimePrices(tickers);
      const priceMap = {};
      for (const p of priceRes.data) priceMap[p.ticker] = p;
      const updated = applyRealtimePrices(currentData, priceMap);
      rawDataRef.current = updated;
      setData(updated);
      setPriceSource(priceRes.data.some(p => p.source === 'cached') ? 'mixed' : 'realtime');
    } catch {
      setPriceSource(null);
    } finally {
      setPriceLoading(false);
    }
  }, []);

  // ── 데이터 조회 ────────────────────────────────────────────
  const handleSearch = useCallback(async (resetSort = false) => {
    setLoading(true);
    setPriceSource(null);
    if (resetSort) { setSortKey(''); setSortDir('asc'); }
    try {
      const params = { include_sold: inclSold };
      if (filterAccount.length) params.account_name = filterAccount.join(',');
      const res = await fetchTurnStatus(params);
      const rows = res.data ?? [];
      rawDataRef.current = rows;
      setData(rows);
      setMaxTurnNo(res.max_turn_no ?? 0);
      showMessage(res.message || `${rows.length}종목 조회되었습니다.`);
      runRealtimeUpdate(rows);
    } catch {
      showMessage('조회에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [filterAccount, inclSold, runRealtimeUpdate]);

  const handlePopupSaved = useCallback(() => {
    setPopup(null);
    handleSearch();
  }, [handleSearch]);

  // 조건 변경 시 자동 재조회 (마운트 포함)
  useEffect(() => {
    handleSearch(false);
  }, [handleSearch]);

  // ── 자동갱신 (3분 interval) ────────────────────────────────
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      if (rawDataRef.current.length > 0) runRealtimeUpdate(rawDataRef.current);
    }, 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, [autoRefresh, runRealtimeUpdate]);

  // ── 수동 시세갱신 ─────────────────────────────────────────
  const handleManualRefresh = useCallback(async () => {
    if (isRefreshing || priceLoading || !rawDataRef.current.length) return;
    setIsRefreshing(true);
    try {
      await runRealtimeUpdate(rawDataRef.current);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, priceLoading, runRealtimeUpdate]);

  // ── 정렬 ──────────────────────────────────────────────────
  const handleSort = key => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const va = sortKey === 'stock_name' ? (a.stock_name || a.ticker) : a[sortKey];
      const vb = sortKey === 'stock_name' ? (b.stock_name || b.ticker) : b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc'
        ? String(va).localeCompare(String(vb), 'ko')
        : String(vb).localeCompare(String(va), 'ko');
    });
  }, [data, sortKey, sortDir]);

  const turnCols = useMemo(() => {
    const cols = [1, 2, 3, 4, 5];
    for (let n = 6; n <= maxTurnNo; n++) cols.push(n);
    return cols;
  }, [maxTurnNo]);

  const totalCols = 4 + 6 + 1 + 1 + turnCols.length + 1;

  // ── 컬럼 리사이즈 ─────────────────────────────────────────
  const startResize = (e, colId) => {
    e.stopPropagation();
    e.preventDefault();
    const minWMap = { account: 70, term: 50, sector: 60, name: 100 };
    resizingRef.current = { colId, startX: e.clientX, startW: colW[colId], minW: minWMap[colId] };
    const onMove = ev => {
      if (!resizingRef.current) return;
      const { colId: cid, startX, startW, minW } = resizingRef.current;
      setColW(prev => ({ ...prev, [cid]: Math.max(minW, startW + (ev.clientX - startX)) }));
    };
    const onUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // ── 정렬 헤더 헬퍼 ────────────────────────────────────────
  const SortTh = ({ col, children, className = '', style = {} }) => (
    <th
      className={`sortable-th ${sortKey === col ? 'sort-col-active' : ''} ${className}`}
      style={{ position: 'relative', ...style }}
      onClick={() => handleSort(col)}
    >
      {children}
      <SortIcon col={col} sortKey={sortKey} sortDir={sortDir} />
    </th>
  );

  // ── 툴바 ──────────────────────────────────────────────────
  const toolbar = (
    <div className="turn-filter">
      <MultiSelectCombo
        options={accounts.map(a => ({ value: a.account_name, label: a.account_name }))}
        value={filterAccount}
        onChange={setFilterAccount}
        placeholder="전체"
      />
      <button className="btn btn-primary" onClick={() => handleSearch(true)} disabled={loading}>
        {loading ? '조회 중...' : '조회(재정렬)'}
      </button>
      <label className="turn-chk">
        <input type="checkbox" checked={inclSold} onChange={e => setInclSold(e.target.checked)} />
        매도완료종목포함
      </label>

      <span className="turn-sep" />

      {[
        { value: 'rate',  label: '수익률' },
        { value: 'date',  label: '날짜' },
        { value: 'qty',   label: '수량' },
        { value: 'price', label: '매입단가' },
      ].map(({ value, label }) => (
        <button
          key={value}
          className={`turn-mode-btn ${displayMode === value ? 'active' : ''}`}
          onClick={() => setDisplayMode(value)}
        >
          {label}
        </button>
      ))}

      {!autoRefresh && priceLoading  && <span className="turn-price-status loading">시세 조회 중...</span>}
      {!autoRefresh && !priceLoading && priceSource === 'realtime' && <span className="turn-price-status realtime">● 실시간 시세</span>}
      {!autoRefresh && !priceLoading && priceSource === 'mixed'    && <span className="turn-price-status mixed">● 캐시 시세 포함</span>}

      <label className={`auto-refresh-chk ${autoRefresh ? 'active' : ''} ${autoRefresh && priceLoading ? 'fetching' : ''}`}>
        <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
        {autoRefresh && priceLoading ? '시세조회중...' : '자동갱신(3분)'}
      </label>
      <button
        className={`turn-refresh-btn${(isRefreshing || priceLoading) ? ' spinning' : ''}`}
        onClick={handleManualRefresh}
        disabled={isRefreshing || priceLoading || !data.length}
        title="시세 수동 갱신"
      >
        ↻
      </button>
    </div>
  );

  // ── JSX ───────────────────────────────────────────────────
  return (
    <>
    <PageLayout title="차수별현황" toolbar={toolbar} message={message}>
      <div className="turn-body">

        <div className="turn-grid-wrapper">
          <table className="grid-table turn-grid">
            <thead>
              <tr>
                {/* ── 좌측 고정 4컬럼 (리사이즈 가능) */}
                <SortTh col="account_name" className="th-freeze-left"
                  style={{ position: 'sticky', left: stickyLeft.account, minWidth: colW.account, width: colW.account }}>
                  계좌
                  <span className="col-resize-handle" onMouseDown={e => startResize(e, 'account')} />
                </SortTh>
                <SortTh col="term_type" className="th-freeze-left col-center"
                  style={{ position: 'sticky', left: stickyLeft.term, minWidth: colW.term, width: colW.term }}>
                  장단기
                  <span className="col-resize-handle" onMouseDown={e => startResize(e, 'term')} />
                </SortTh>
                <SortTh col="sector_name" className="th-freeze-left"
                  style={{ position: 'sticky', left: stickyLeft.sector, minWidth: colW.sector, width: colW.sector }}>
                  섹터
                  <span className="col-resize-handle" onMouseDown={e => startResize(e, 'sector')} />
                </SortTh>
                <SortTh col="stock_name" className="th-freeze-left"
                  style={{ position: 'sticky', left: stickyLeft.name, minWidth: colW.name, width: colW.name }}>
                  종목명
                  <span className="col-resize-handle" onMouseDown={e => startResize(e, 'name')} />
                </SortTh>

                {/* ── 합계 컬럼 6개 (sticky) */}
                <th className="col-right th-summary th-freeze-left"
                  style={{ left: stickyLeft.qty, minWidth: COL_QTY_W, width: COL_QTY_W }}>수량</th>
                <th className="col-right th-summary th-freeze-left"
                  style={{ left: stickyLeft.buyPrice, minWidth: COL_BPRICE_W, width: COL_BPRICE_W }}>매입가</th>
                <th className="col-right th-summary th-freeze-left"
                  style={{ left: stickyLeft.buyAmt, minWidth: COL_BAMT_W, width: COL_BAMT_W }}>매입금액</th>
                <th className="col-right th-summary th-freeze-left"
                  style={{ left: stickyLeft.evalAmt, minWidth: COL_EAMT_W, width: COL_EAMT_W }}>평가금액</th>
                <th className="col-right th-summary th-freeze-left"
                  style={{ left: stickyLeft.evalProfit, minWidth: COL_EPRFT_W, width: COL_EPRFT_W }}>평가손익</th>
                <th className="col-right th-summary th-freeze-left th-summary-last"
                  style={{ left: stickyLeft.totalRate, minWidth: COL_AGGRATE_W, width: COL_AGGRATE_W }}>수익률</th>

                {/* ── 현재가, 등락 */}
                <th className="col-right th-price" style={{ minWidth: COL_PRICE_W, width: COL_PRICE_W }}>현재가</th>
                <SortTh col="diff_rate" className="col-center th-diff"
                  style={{ minWidth: COL_DIFF_W, width: COL_DIFF_W, borderRight: '2px solid #bbb' }}>등락</SortTh>

                {/* ── 차수 컬럼 */}
                {turnCols.map(n => (
                  <th key={n} className="col-center th-turn"
                    style={{ minWidth: COL_TURN_W, width: COL_TURN_W }}>{n}차</th>
                ))}

                {/* ── 우측 고정: 비고 */}
                <SortTh col="invest_memo" className="th-freeze-right"
                  style={{ position: 'sticky', right: 0, minWidth: COL_MEMO_W }}>비고</SortTh>
              </tr>
            </thead>
            <tbody>
              {sortedData.length === 0 ? (
                <tr>
                  <td colSpan={totalCols} className="grid-empty">
                    {loading ? '조회 중...' : '조회 버튼을 눌러 데이터를 불러오세요.'}
                  </td>
                </tr>
              ) : (
                sortedData.map((row, idx) => {
                  const { totalQty, avgBuyPrice, totalBuyAmt, totalEval, totalProfit, totalRate } = computeTotals(row);
                  return (
                    <tr key={idx} className={row.is_all_sold ? 'row-all-sold' : 'row-active'}>
                      {/* ── 좌측 고정 4컬럼 */}
                      <td className="td-freeze-left td-account"
                        style={{ left: stickyLeft.account, width: colW.account, minWidth: colW.account }}>
                        {row.account_name}
                      </td>
                      <td className="td-freeze-left col-center td-term"
                        style={{ left: stickyLeft.term, width: colW.term, minWidth: colW.term }}>
                        {row.term_type || ''}
                      </td>
                      <td className="td-freeze-left td-sector"
                        style={{ left: stickyLeft.sector, width: colW.sector, minWidth: colW.sector }}>
                        {row.sector_name || ''}
                      </td>
                      <td className="td-freeze-left td-name"
                        style={{ left: stickyLeft.name, width: colW.name, minWidth: colW.name }}>
                        <div className="stock-name-text stock-chart-link"
                          onClick={() => setChartPopup({
                            ticker:       row.ticker,
                            stockName:    row.stock_name || row.ticker,
                            accountName:  row.account_name,
                            termType:     row.term_type,
                            currentPrice: row.current_price,
                            diffRate:     row.diff_rate,
                            turns: Object.entries(row.turns || {})
                              .filter(([, t]) => t.type === 'unsold')
                              .map(([n, t]) => ({
                                turn_no:     Number(n),
                                buy_date:    t.buy_date,
                                buy_price:   t.buy_price,
                                quantity:    t.quantity,
                                profit_rate: t.profit_rate,
                              }))
                              .sort((a, b) => a.turn_no - b.turn_no),
                          })}>
                          {row.stock_name || row.ticker}
                        </div>
                        <div className="stock-ticker-text">{row.ticker}</div>
                      </td>

                      {/* ── 합계 컬럼 6개 (sticky) */}
                      <td className="col-right td-summary td-freeze-left"
                        style={{ left: stickyLeft.qty, width: COL_QTY_W, minWidth: COL_QTY_W }}>{fQty(totalQty)}</td>
                      <td className="col-right td-summary td-freeze-left"
                        style={{ left: stickyLeft.buyPrice, width: COL_BPRICE_W, minWidth: COL_BPRICE_W }}>{fPrice(avgBuyPrice)}</td>
                      <td className="col-right td-summary td-freeze-left"
                        style={{ left: stickyLeft.buyAmt, width: COL_BAMT_W, minWidth: COL_BAMT_W }}>{fAmt(totalBuyAmt)}</td>
                      <td className="col-right td-summary td-freeze-left"
                        style={{ left: stickyLeft.evalAmt, width: COL_EAMT_W, minWidth: COL_EAMT_W }}>{fAmt(totalEval)}</td>
                      <td className="col-right td-summary td-freeze-left"
                        style={{ left: stickyLeft.evalProfit, width: COL_EPRFT_W, minWidth: COL_EPRFT_W, ...getDiffStyle(totalProfit) }}>{fAmt(totalProfit)}</td>
                      <td className="col-right td-summary td-freeze-left td-summary-last"
                        style={{ left: stickyLeft.totalRate, width: COL_AGGRATE_W, minWidth: COL_AGGRATE_W, ...getDiffStyle(totalRate) }}>{fRate(totalRate)}</td>

                      {/* ── 현재가, 등락 */}
                      <td className="col-right td-price">{fPrice(row.current_price)}</td>
                      <td className="col-center td-diff"
                        style={{ ...getDiffStyle(row.diff_rate), borderRight: '2px solid #bbb' }}>
                        {row.diff_rate != null ? fRate(row.diff_rate) : ''}
                      </td>

                      {/* ── 차수 셀 */}
                      {turnCols.map(n => {
                        const turn     = row.turns?.[String(n)];
                        const isUnsold = turn && turn.type === 'unsold';
                        const isOrange = row.orange_cell && row.orange_cell.turn_no === n;

                        // 오렌지 셀: 수익률 모드에서만 표시, 클릭 불가
                        if (isOrange) {
                          return (
                            <td key={n} className="col-center td-turn td-turn-orange">
                              {displayMode === 'rate' && row.orange_cell.change_rate != null
                                ? <span style={{ color: '#e67e22' }}>{fRate(row.orange_cell.change_rate)}</span>
                                : null}
                            </td>
                          );
                        }

                        let content = '';
                        if (isUnsold) {
                          if      (displayMode === 'date')  content = formatBuyDate(turn.buy_date);
                          else if (displayMode === 'qty')   content = turn.quantity  != null ? fQty(turn.quantity)   : '';
                          else if (displayMode === 'rate')  content = turn.profit_rate != null ? fRate(turn.profit_rate) : '';
                          else if (displayMode === 'price') content = turn.buy_price != null ? fPrice(turn.buy_price) : '';
                        }

                        const colorStyle = isUnsold && displayMode === 'rate' && turn.profit_rate != null
                          ? { color: turn.profit_rate >= 0 ? '#FF0000' : '#0000FF' }
                          : {};

                        const hasAlertForTurn = isUnsold &&
                          (turn.alert_type_id_up != null || turn.alert_type_id_down != null);

                        return (
                          <td
                            key={n}
                            className={`col-center td-turn ${isUnsold ? 'td-turn-sell' : 'td-turn-buy'}`}
                            onClick={() => isUnsold ? openSellPopup(row, turn, n) : openBuyPopup(row)}
                          >
                            {content ? <span style={colorStyle}>{content}</span> : null}
                            {hasAlertForTurn && (
                              <AlertButton
                                hasAlert={true}
                                alertEnabled={!!turn.alert_enabled}
                                alertTypeNameUp={turn.alert_type_name_up}
                                alertTypeNameDown={turn.alert_type_name_down}
                                onClick={() => setAlertPopup({ buyId: turn.buy_id, ticker: row.ticker, stockName: row.stock_name, accountName: row.account_name, turnNo: n })}
                              />
                            )}
                          </td>
                        );
                      })}

                      {/* ── 우측 고정: 비고 */}
                      <td className="td-freeze-right td-memo">{row.invest_memo || ''}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

      </div>
    </PageLayout>

    {popup?.type === 'sell' && <TurnSellPopup data={popup.data} onClose={closePopup} onSaved={handlePopupSaved} />}
    {popup?.type === 'buy'  && <TurnBuyPopup  data={popup.data} onClose={closePopup} onSaved={handlePopupSaved} />}
    {chartPopup && (
      <StockChartPopup
        ticker={chartPopup.ticker}
        stockName={chartPopup.stockName}
        accountName={chartPopup.accountName}
        termType={chartPopup.termType}
        currentPrice={chartPopup.currentPrice}
        diffRate={chartPopup.diffRate}
        turns={chartPopup.turns || []}
        onClose={() => setChartPopup(null)}
      />
    )}
    {alertPopup && (
      <AlertQuickPopup
        buyId={alertPopup.buyId}
        ticker={alertPopup.ticker}
        stockName={alertPopup.stockName}
        accountName={alertPopup.accountName}
        turnNo={alertPopup.turnNo}
        onClose={() => setAlertPopup(null)}
        onSaved={() => { setAlertPopup(null); handleSearch(); }}
      />
    )}
    </>
  );
};

export default TurnStatusPage;
